// What the plan list shows when the current filter/search yields no rows.
// Pure module (no Angular imports) so it can be unit-tested in the Node
// Jasmine harness, mirroring simple-project.ts.

export interface PlanListEmptyState {
    icon: string; // font-awesome icon class, e.g. 'fa-search'
    title: string;
    message: string;
    showCreate: boolean;
    showClearSearch: boolean;
}

export interface PlanListEmptyStateInput {
    filter: string; // 'all' | 'mine' | 'shared' | 'deleted'
    searchActive: boolean;
    myCount: number;
    sharedCount: number;
    deletedCount: number;
    // Whether any AI feature is enabled for this deployment; the welcome copy
    // must not promise the AI Planner on tenants that have it switched off.
    aiEnabled: boolean;
}

// Callers only invoke this when the filtered list is empty; the input counts
// describe the unfiltered lists so the message can say why it is empty.
export function computePlanListEmptyState(input: PlanListEmptyStateInput): PlanListEmptyState {

    if (input.searchActive) {
        return {
            icon: 'fa-search',
            title: 'No matching plans',
            message: 'No plans match your search.',
            showCreate: false,
            showClearSearch: true,
        };
    }

    const hasAnyPlans = input.myCount + input.sharedCount + input.deletedCount > 0;
    // One empty state, not two. The second one existed for a user who could not
    // create a plan, and nobody is in that position now: plan creation is not
    // named by any capability, so it is assumed on for everyone.
    if (!hasAnyPlans) {
        return {
            icon: 'fa-map-o',
            title: "Let's build your first plan",
            message: input.aiEnabled
                ? 'Describe your project and the AI Planner will draft a schedule you can shape, or start from a blank plan.'
                : 'Create your first plan and build your schedule on the whiteboard.',
            showCreate: true,
            showClearSearch: false,
        };
    }

    switch (input.filter) {
        case 'mine':
            if (input.deletedCount > 0) {
                return {
                    icon: 'fa-folder-open-o',
                    title: 'No active plans of your own',
                    message: 'Your archived plans are under Archived.',
                    showCreate: true,
                    showClearSearch: false,
                };
            }
            return {
                icon: 'fa-folder-open-o',
                title: 'No plans of your own yet',
                message: 'Plans you create belong to you and appear here.',
                showCreate: true,
                showClearSearch: false,
            };
        case 'shared':
            return {
                icon: 'fa-users',
                title: 'Nothing shared with you yet',
                message: 'When someone shares a plan with you, it appears here.',
                showCreate: false,
                showClearSearch: false,
            };
        case 'deleted':
            return {
                icon: 'fa-archive',
                title: 'No archived plans',
                message: 'Plans you archive are kept here and can be restored at any time.',
                showCreate: false,
                showClearSearch: false,
            };
        default:
            // 'all' but every remaining plan is archived.
            return {
                icon: 'fa-folder-open-o',
                title: 'No active plans',
                message: 'Restore a plan from Archived, or create a new one.',
                showCreate: true,
                showClearSearch: false,
            };
    }
}
